"use client";

import { useEffect, useRef, useState } from "react";

type Options = {
  rootMargin?: string;
  threshold?: number;
};

/**
 * One-shot IntersectionObserver hook. Returns `[ref, inView]` where `inView`
 * flips true on the first intersection and stays true (observer disconnects).
 * Respects prefers-reduced-motion by reporting in-view immediately so consumers
 * skip their entrance transition.
 */
export function useInView<T extends HTMLElement>(opts: Options = {}) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      {
        rootMargin: opts.rootMargin ?? "0px 0px -10% 0px",
        threshold: opts.threshold ?? 0.15,
      },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [opts.rootMargin, opts.threshold]);

  return [ref, inView] as const;
}
